import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Sku } from './types';

export type CartItem = {
  skuId: string;
  number: number;
  title: string;
  price: number;
  imageUrl: string | null;
  stock: number;
  count: number;
};

type CartState = {
  items: CartItem[];
  add: (sku: Sku, count?: number) => void;
  setCount: (skuId: string, count: number) => void;
  remove: (skuId: string) => void;
  clear: () => void;
};

const clamp = (count: number, stock: number): number => Math.max(1, Math.min(count, stock));

export const useCart = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      add: (sku, count = 1) =>
        set((s) => {
          const existing = s.items.find((i) => i.skuId === sku.id);
          if (existing) {
            return {
              items: s.items.map((i) => (i.skuId === sku.id ? { ...i, count: clamp(i.count + count, sku.stock) } : i)),
            };
          }
          const item: CartItem = {
            skuId: sku.id,
            number: sku.number,
            title: sku.title,
            price: sku.price,
            imageUrl: sku.imageUrls[0] ?? null,
            stock: sku.stock,
            count: clamp(count, sku.stock),
          };
          return { items: [...s.items, item] };
        }),
      setCount: (skuId, count) =>
        set((s) => ({
          items: s.items.map((i) => (i.skuId === skuId ? { ...i, count: clamp(count, i.stock) } : i)),
        })),
      remove: (skuId) => set((s) => ({ items: s.items.filter((i) => i.skuId !== skuId) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'cult-cart' },
  ),
);

export const selectCartCount = (s: CartState): number => s.items.reduce((n, i) => n + i.count, 0);
export const selectCartTotal = (s: CartState): number => s.items.reduce((sum, i) => sum + i.price * i.count, 0);
